/**
 * The wagon wheel, as arithmetic (ADR-0022). Pure, like everything in this package: the scorer,
 * the server-rendered public page and the React Native app all call these, so "cover" means the
 * same sector on every surface and a left-hander's labels can only be wrong in one place.
 *
 * ## The geometry, and why it is stored this way
 *
 * A shot is a point in polar form around the striker: `wagonAngle` degrees clockwise from the
 * bowler's end, `wagonDistance` as a share of the boundary. That is where the ball physically
 * went, seen from behind the bowler's arm. Which *cricketing* region that is depends on the
 * batter, because the off side is the bat's side: for a right-hander it is the right of the
 * screen, for a left-hander the left. So the zone is computed from the angle and the hand
 * together and is never stored. The hand is not on the shot either: it is on the ledger event that
 * brought the batter in (ADR-0023), and replay puts it on each ball it belongs to. A hand entered
 * wrongly is then fixed by correcting that one event, with no loss of where any ball went.
 *
 * The eight sectors are 45° each, centred on the named fielding positions of a right-hander.
 * They are a reading aid, not a fielding chart: grassroots grounds have no fixed positions.
 */
import type { BatterHand, Shot } from "./events.ts";
import type { OverStripEntry } from "./state.ts";
import type { PlayerId } from "./ids.ts";

/**
 * The sectors in clockwise order from straight down the ground, *as a right-hander sees them*.
 * Index 0 is the sector from 0° to 45°.
 */
export const ZONES = [
  "Long off",
  "Cover",
  "Point",
  "Third man",
  "Fine leg",
  "Square leg",
  "Mid-wicket",
  "Long on",
] as const;

export type Zone = (typeof ZONES)[number];

/** The largest distance a six is drawn at: just past the rope. */
export const SIX_DISTANCE = 1.1;

const norm = (angle: number): number => ((angle % 360) + 360) % 360;

/** A left-hander's world is the right-hander's, mirrored left to right. */
const relative = (angle: number, hand: BatterHand): number =>
  hand === "left" ? norm(360 - angle) : norm(angle);

/** Which sector, as an index into `ZONES`. */
export const zoneIndex = (angle: number, hand: BatterHand): number =>
  Math.floor(relative(angle, hand) / 45) % 8;

export const zoneOf = (angle: number, hand: BatterHand): Zone => ZONES[zoneIndex(angle, hand)] as Zone;

/** Off side is the half the bat faces, wherever it is on the screen. */
export const sideOf = (angle: number, hand: BatterHand): "off" | "leg" =>
  relative(angle, hand) < 180 ? "off" : "leg";

/**
 * Turn a touch into a shot.
 *
 * `dx` and `dy` are offsets from the striker in any consistent unit with `radius` the boundary
 * in that same unit, `dy` growing downwards as on a screen. The direction is what the scorer
 * chose; the distance is mostly decided for them, because the only distances anyone can mean
 * are "to the rope" and "beyond it" for boundaries, and a rough short, medium or long for the
 * rest. Snapping 4s and 6s is what lets a thumb on a bright day be wrong by a few pixels
 * without the data being wrong.
 */
export function shotFromOffset(dx: number, dy: number, radius: number, runs: number): Shot {
  const angle = norm((Math.atan2(dx, -dy) * 180) / Math.PI);
  const touched = Math.min(1, Math.hypot(dx, dy) / radius);
  const wagonDistance =
    runs >= 6 ? SIX_DISTANCE : runs === 4 ? 1 : Math.max(0.22, Math.min(0.94, touched));
  return {
    wagonAngle: Math.round(angle),
    wagonDistance: Math.round(wagonDistance * 100) / 100,
    shotType: null,
  };
}

/** One line on the wheel. */
export interface WagonLine {
  readonly eventId: string;
  readonly batterId: PlayerId;
  readonly runs: number;
  readonly angle: number;
  readonly distance: number;
  readonly hand: BatterHand;
  readonly zone: Zone;
  readonly side: "off" | "leg";
}

export interface WagonFilter {
  readonly batterId?: PlayerId;
  /** Only shots worth exactly this many runs off the bat. */
  readonly runs?: number;
}

export interface WagonWheel {
  readonly lines: readonly WagonLine[];
  /** Runs off the bat per sector, in `ZONES` order. */
  readonly runsByZone: readonly number[];
  readonly offRuns: number;
  readonly legRuns: number;
  /** Share of runs on the off side as a whole percentage, or null with no runs. */
  readonly offPercent: number | null;
  readonly fours: number;
  readonly sixes: number;
  readonly totalRuns: number;
  /**
   * Deliveries that were scored off the bat but could not be placed: no direction was recorded, or
   * the batter's hand was never recorded (ADR-0023), which a left- and a right-hander's cover need
   * to tell apart. Unknown is counted here and never drawn as right-handed.
   */
  readonly unplaced: number;
}

/**
 * Whether a recorded shot can be drawn.
 *
 * The server never rejects a ball (ADR-0011), so a `shot` read back from the ledger is whatever a
 * client sent, and an angle that is not a number would print as `NaN` into a drawing on the public
 * page. A shot that cannot be placed is counted as unplaced, which is what it is.
 */
function drawable(shot: Shot): boolean {
  return (
    Number.isFinite(shot.wagonAngle) &&
    Number.isFinite(shot.wagonDistance) &&
    shot.wagonDistance >= 0 &&
    shot.wagonDistance <= SIX_DISTANCE
  );
}

/**
 * The wheel for a set of balls.
 *
 * Only runs off the bat belong on it: byes, leg byes and wides were not hit, and a wide's
 * direction says nothing about the batter. A ball with runs off the bat and no shot is counted
 * in `unplaced` so a page can say "14 of 20 scoring shots placed" rather than quietly presenting
 * a partial wheel as the whole picture.
 */
export function wagonWheel(
  deliveries: readonly OverStripEntry[],
  filter: WagonFilter = {},
): WagonWheel {
  const lines: WagonLine[] = [];
  const runsByZone = ZONES.map(() => 0);
  let offRuns = 0;
  let legRuns = 0;
  let fours = 0;
  let sixes = 0;
  let unplaced = 0;

  for (const ball of deliveries) {
    const runs = ball.runs.batter;
    if (runs <= 0) continue;
    if (filter.batterId !== undefined && ball.batterId !== filter.batterId) continue;
    if (filter.runs !== undefined && runs !== filter.runs) continue;
    if (ball.shot === null || !drawable(ball.shot) || ball.batterHand === null) {
      unplaced += 1;
      continue;
    }
    const { wagonAngle: rawAngle, wagonDistance } = ball.shot;
    const batterHand = ball.batterHand;
    const wagonAngle = norm(rawAngle);
    const index = zoneIndex(wagonAngle, batterHand);
    const side = sideOf(wagonAngle, batterHand);
    lines.push({
      eventId: String(ball.eventId),
      batterId: ball.batterId,
      runs,
      angle: wagonAngle,
      distance: wagonDistance,
      hand: batterHand,
      zone: ZONES[index] as Zone,
      side,
    });
    runsByZone[index] = (runsByZone[index] ?? 0) + runs;
    if (side === "off") offRuns += runs;
    else legRuns += runs;
    if (runs === 4) fours += 1;
    if (runs >= 6) sixes += 1;
  }

  const totalRuns = offRuns + legRuns;
  return {
    lines,
    runsByZone,
    offRuns,
    legRuns,
    offPercent: totalRuns === 0 ? null : Math.round((offRuns * 100) / totalRuns),
    fours,
    sixes,
    totalRuns,
    unplaced,
  };
}

// ── Drawing ─────────────────────────────────────────────────────────────────
//
// The geometry of the picture lives here, not in either renderer. The scorer draws it with
// React and the public page draws it as a string of SVG with no script (ADR-0007); if each
// owned its own copy of "where is cover" they would disagree within a release.

/** The drawing's coordinate space: a square viewBox `0 0 300 300`, the striker at the centre. */
export const GROUND = { size: 300, centre: 150, radius: 132, inner: 62 } as const;

const round1 = (n: number): number => Math.round(n * 10) / 10;

/** Where a shot ends, in viewBox units. Angle 0 is straight up the screen. */
export function pointAt(angle: number, distance: number): { readonly x: number; readonly y: number } {
  const rad = (angle * Math.PI) / 180;
  return {
    x: round1(GROUND.centre + Math.sin(rad) * GROUND.radius * distance),
    y: round1(GROUND.centre - Math.cos(rad) * GROUND.radius * distance),
  };
}

/** The wedge for one physical 45° sector, 0 being the one clockwise of straight. */
export function sectorPath(sector: number): string {
  const edge = 1.03;
  const a = pointAt(sector * 45, edge);
  const b = pointAt(sector * 45 + 45, edge);
  const r = round1(GROUND.radius * edge);
  return `M${GROUND.centre} ${GROUND.centre}L${a.x} ${a.y}A${r} ${r} 0 0 1 ${b.x} ${b.y}Z`;
}

/** Which physical sector a zone is drawn in. A left-hander's zones sit mirrored. */
export const sectorOfZone = (zone: number, hand: BatterHand): number => (hand === "left" ? 7 - zone : zone);

/**
 * How a set of lines is laid on the ground.
 *
 * One batter, or several of the same hand, is drawn where the balls physically went and labelled
 * for that hand. A mixed set is drawn **normalised**: every left-hander's shot is mirrored, so
 * the whole picture reads as a right-hander's and "cover" means one place. Anything else would
 * put a left-hander's cover drives under the label "mid-wicket".
 */
export type GroundView = { readonly hand: BatterHand; readonly normalised: boolean };

export function viewOf(lines: readonly WagonLine[], hand: BatterHand | null): GroundView {
  if (hand !== null) return { hand, normalised: false };
  const hands = new Set(lines.map((line) => line.hand));
  if (hands.size <= 1) return { hand: lines[0]?.hand ?? "right", normalised: false };
  return { hand: "right", normalised: true };
}

/** The angle a line is drawn at in a given view. */
export const drawnAngle = (line: WagonLine, view: GroundView): number =>
  view.normalised && line.hand === "left" ? norm(360 - line.angle) : line.angle;
